import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from "@mui/material";
import KpiCard from "../commercial-dashboard/KpiCard";
import { KpiGrid, Section } from "../commercial-dashboard/DashboardBits";
import {
  CHANNEL_COLORS,
  CHANNELS,
  CM_CHANNELS,
  MONTHS,
  aggregate,
  bridge,
  eur,
  eurSigned,
  groupBy,
  inPeriod,
  isPaidMedia,
  keur,
  peopleLabel,
  pct,
  pctSigned,
  pp,
  subformatLabel,
  sum,
  toneOfAmount,
  toneOfCompliance,
  toneOfMarginDiff,
} from "./controlBoardModel";
import {
  Caret,
  ChannelSwatch,
  ComplianceBar,
  Donut,
  MixBar,
  Note,
  Num,
  PeriodSelector,
  ShareLegend,
  ShareRing,
  Signal,
  TypeLegend,
  useToneColor,
} from "./ControlBoardBits";
import { TabProps } from "./boardData";
import ClientDetailTables from "./ClientDetailTables";
import { ControlBoardChannel } from "../../types/controlBoard";

interface Props extends TabProps {
  clientId: string;
  onClientChange: (clientId: string) => void;
}

/** Por cliente: sin cliente, el ranking frente al BP; con cliente, su detalle completo. */
const ClientTab: React.FC<Props> = ({
  data,
  clientId,
  onClientChange,
  onOpenClient,
}) => {
  const theme = useTheme();
  const toneColor = useToneColor();
  const [month, setMonth] = useState(0);
  const [companyId, setCompanyId] = useState("");
  const [open, setOpen] = useState<Set<string>>(new Set());
  const [rankSort, setRankSort] = useState<"real" | "compliance" | "shift">(
    "real"
  );

  useEffect(() => setCompanyId(""), [clientId]);

  const toggle = (key: string) =>
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const period = month
    ? `en ${MONTHS[month].toLowerCase()}`
    : `a ${MONTHS[data.lastMonth].toLowerCase()}`;

  const clientRows = useMemo(
    () => data.rows.filter((r) => r.clientId === clientId),
    [data, clientId]
  );
  const companies = useMemo(
    () =>
      [...groupBy(clientRows, (r) => r.companyId).entries()]
        .map(([id, list]) => ({
          id,
          name: list[0].company,
          sale: sum(list, (r) => r.sale),
        }))
        .sort((a, b) => b.sale - a.sale),
    [clientRows]
  );
  const allRows = useMemo(
    () =>
      companyId
        ? clientRows.filter((r) => r.companyId === companyId)
        : clientRows,
    [clientRows, companyId]
  );
  const rows = useMemo(
    () => inPeriod(allRows, month, data.lastMonth),
    [allRows, month, data.lastMonth]
  );
  const clientPlan = useMemo(
    () =>
      data.plan.filter(
        (l) =>
          data.clientOf(l.companyId, l.companyName || l.clientName) ===
            clientId &&
          (!companyId || l.companyId === companyId)
      ),
    [data, clientId, companyId]
  );
  const plan = useMemo(
    () => inPeriod(clientPlan, month, data.lastMonth),
    [clientPlan, month, data.lastMonth]
  );

  const header = (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        borderRadius: 3,
        border: `1px solid ${theme.palette.grey[200]}`,
      }}
    >
      <Stack
        direction={{ xs: "column", md: "row" }}
        gap={2}
        alignItems={{ md: "center" }}
      >
        <FormControl size="small" sx={{ minWidth: 260 }}>
          <InputLabel id="cb-client">Cliente</InputLabel>
          <Select
            labelId="cb-client"
            label="Cliente"
            value={clientId}
            onChange={(e) => onClientChange(e.target.value)}
          >
            <MenuItem value="">Todos los clientes · resumen</MenuItem>
            {data.clients.map((c) => (
              <MenuItem key={c.id} value={c.id}>
                {c.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {companies.length > 1 && (
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel id="cb-company">Marca</InputLabel>
            <Select
              labelId="cb-company"
              label="Marca"
              value={companyId}
              onChange={(e) => setCompanyId(e.target.value)}
            >
              <MenuItem value="">Todas las marcas</MenuItem>
              {companies.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        <Box sx={{ flex: 1 }} />
        <PeriodSelector
          value={month}
          lastMonth={data.lastMonth}
          onChange={setMonth}
        />
      </Stack>
    </Paper>
  );

  if (!clientId) {
    return (
      <Stack gap={2}>
        {header}
        <Ranking
          data={data}
          month={month}
          sort={rankSort}
          onSort={setRankSort}
          onOpenClient={onOpenClient}
          period={period}
        />
      </Stack>
    );
  }

  const totals = aggregate(rows);
  const b = bridge(rows, plan);
  const planFee = sum(plan, (l) => l.fee);
  const compliance = b.planInvestment
    ? b.realInvestment / b.planInvestment
    : NaN;
  const profitCompliance = b.planProfit ? b.realProfit / b.planProfit : NaN;
  const shift = b.realShareCM - b.planShareCM;
  const profitDiff = b.realProfit - b.planProfit;
  const clientLabel = companyId
    ? companies.find((c) => c.id === companyId)?.name
    : data.clientName(clientId);

  const team = (type: "CM" | "PM") => {
    const list = rows.filter((r) => r.type === type);
    const people = [...new Set(list.flatMap((r) => r.technicians))];
    return peopleLabel(people);
  };
  const accounts = peopleLabel([...new Set(rows.flatMap((r) => r.accounts))]);
  const biggest = CHANNELS.map((c) => ({
    c,
    d: b.channels[c].real.sale - b.channels[c].planShare * b.realInvestment,
  })).sort((x, y) => Math.abs(y.d) - Math.abs(x.d))[0];

  const shown = CHANNELS.filter(
    (c) => b.channels[c].planInvestment > 0 || b.channels[c].real.sale > 0
  );

  return (
    <Stack gap={2}>
      {header}

      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          borderRadius: 3,
          border: `1px solid ${theme.palette.grey[200]}`,
        }}
      >
        <Box
          sx={{
            display: "grid",
            gap: 1.5,
            gridTemplateColumns: {
              xs: "1fr 1fr",
              md: "repeat(4, minmax(0,1fr))",
            },
            mb: 2,
          }}
        >
          {[
            ["Account", accounts],
            ["Técnico compra de medios", team("CM")],
            ["Técnico Paid Media", team("PM")],
            [
              "Peso de medios",
              `${pct(totals.sale ? totals.saleCM / totals.sale : NaN, 0)} compra de medios · ${pct(totals.sale ? totals.salePM / totals.sale : NaN, 0)} Paid Media`,
            ],
          ].map(([label, value]) => (
            <Box
              key={label}
              sx={{
                backgroundColor: theme.palette.grey[50],
                borderRadius: 2,
                p: 1.5,
              }}
            >
              <Typography variant="caption" color="text.secondary">
                {label}
              </Typography>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                {value}
              </Typography>
            </Box>
          ))}
        </Box>
        <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
          {clientLabel} ha facturado {keur(b.realInvestment)}
          {b.planInvestment
            ? ` frente a ${keur(b.planInvestment)} del Business Plan`
            : ""}{" "}
          {period}
          {b.planInvestment ? (
            <>
              , y el beneficio bruto{" "}
              {profitDiff >= 0 ? "supera" : "queda por debajo de"} lo
              planificado en{" "}
              <Box
                component="span"
                sx={{ color: toneColor(profitDiff >= 0 ? "good" : "bad") }}
              >
                {keur(Math.abs(profitDiff))}
              </Box>
              .
            </>
          ) : (
            ". No está en el Business Plan."
          )}
        </Typography>
        {b.planInvestment > 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            El plan llevaba el {pct(b.planShareCM, 0)} de la inversión a compra
            de medios y la realidad va en el {pct(b.realShareCM, 0)} (
            {pp(shift)}): por ese reparto el account{" "}
            {b.mix >= 0 ? "suma" : "resta"} {keur(Math.abs(b.mix))}. Los
            técnicos {b.technician >= 0 ? "han sacado" : "han perdido"}{" "}
            {keur(Math.abs(b.technician))} de margen del margen sobre lo
            vendido.
            {biggest && Math.abs(biggest.d) > 1
              ? ` El canal que más se aparta del plan es ${biggest.c} (${eurSigned(biggest.d)} frente a su peso previsto).`
              : ""}
          </Typography>
        )}
      </Paper>

      <KpiGrid columns={5}>
        <KpiCard
          label="Facturación real"
          value={eur(b.realInvestment)}
          caption={`${b.planInvestment ? `${pct(compliance, 0)} del BP (${keur(b.planInvestment)}) · ` : ""}${pct(totals.compliance, 0)} de lo previsto en briefings`}
        />
        <KpiCard
          label="Beneficio bruto real"
          value={eur(b.realProfit)}
          caption={
            b.planProfit
              ? `${pct(profitCompliance, 0)} del BP (${keur(b.planProfit)})`
              : `Previsto en briefings: ${keur(totals.plannedProfit)}`
          }
        />
        <KpiCard
          label="Inversión en compra de medios"
          value={pct(b.realShareCM, 0)}
          caption={
            Number.isFinite(b.planShareCM)
              ? `${pp(shift)} frente al ${pct(b.planShareCM, 0)} del plan · responsable: account`
              : "Sin plan"
          }
        />
        <KpiCard
          label="Margen del margen"
          value={eurSigned(totals.marginOverSold)}
          caption={`Margen real ${pct(totals.margin)} frente al ${pct(totals.soldMargin)} vendido · responsable: técnico`}
          tone={
            toneOfAmount(totals.marginOverSold, totals.profit) === "bad"
              ? "error"
              : undefined
          }
        />
        <KpiCard
          label="Fee"
          value={eur(totals.fee)}
          caption={`${pct(totals.sale ? totals.fee / totals.sale : NaN, 1)} de la facturación${planFee ? ` · BP ${keur(planFee)}` : ""}`}
        />
      </KpiGrid>

      <Section
        title="Plan de medios: Business Plan frente a real"
        subtitle="Reparto de la inversión por canal en el periodo."
      >
        <Box
          sx={{
            display: "grid",
            gap: 3,
            gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
          }}
        >
          {[
            {
              title: "Business Plan",
              get: (c: ControlBoardChannel) => b.channels[c].planInvestment,
              total: b.planInvestment,
            },
            {
              title: "Real",
              get: (c: ControlBoardChannel) => b.channels[c].real.sale,
              total: b.realInvestment,
            },
          ].map((side) => {
            const parts = CHANNELS.map((c) => ({
              label: c,
              value: side.get(c),
              color: CHANNEL_COLORS[c],
            }));
            return (
              <Stack
                key={side.title}
                direction="row"
                gap={2}
                alignItems="center"
                flexWrap="wrap"
              >
                <Donut
                  parts={parts}
                  centerValue={keur(side.total)}
                  centerLabel={side.title}
                />
                <Box>
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 600, mb: 0.5 }}
                  >
                    {side.title}
                  </Typography>
                  {side.total ? (
                    <ShareLegend parts={parts} />
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      Sin datos
                    </Typography>
                  )}
                </Box>
              </Stack>
            );
          })}
        </Box>
      </Section>

      <Section
        title="Formatos: Business Plan frente a real"
        subtitle="Inversión del plan y la real por canal, el desvío y el margen en sus tres momentos: el del Business Plan, el vendido en los briefings y el real de las plataformas. Pulsa un canal para ver sus subformatos."
      >
        <Box sx={{ overflowX: "auto" }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Formato</TableCell>
                <Num>Inversión BP</Num>
                <Num>Inversión real</Num>
                <Num>Desvío €</Num>
                <Num>Desvío %</Num>
                <Num>Margen BP</Num>
                <Num>Margen briefing</Num>
                <Num>Margen real</Num>
                <Num>Real vs BP</Num>
                <Num>Real vs briefing</Num>
              </TableRow>
            </TableHead>
            <TableBody>
              {shown.map((c) => {
                const ch = b.channels[c];
                const r = ch.real;
                const pm = isPaidMedia(c);
                const d = r.sale - ch.planInvestment;
                const dp = ch.planInvestment
                  ? r.sale / ch.planInvestment - 1
                  : NaN;
                const key = `fmt:${c}`;
                const subs = [
                  ...groupBy(
                    rows.filter((x) => x.channel === c),
                    (x) => x.subformat
                  ).entries(),
                ]
                  .map(([s, list]) => ({ s, t: aggregate(list) }))
                  .sort((x, y) => y.t.sale - x.t.sale);
                const status = !ch.planInvestment
                  ? " · fuera del BP"
                  : !r.sale
                    ? " · no activado"
                    : "";
                return (
                  <React.Fragment key={c}>
                    <TableRow
                      hover
                      onClick={() => toggle(key)}
                      sx={{ cursor: "pointer" }}
                    >
                      <TableCell>
                        <Caret
                          open={open.has(key)}
                          onClick={() => toggle(key)}
                        />
                        <ChannelSwatch channel={c} />
                        <strong>{c}</strong>
                        <Typography
                          component="span"
                          variant="caption"
                          color="text.secondary"
                        >
                          {status}
                        </Typography>
                      </TableCell>
                      <Num muted>{eur(ch.planInvestment)}</Num>
                      <Num>{eur(r.sale)}</Num>
                      <Num>{eurSigned(d)}</Num>
                      <Num>
                        {ch.planInvestment ? (
                          <Signal
                            text={pctSigned(dp)}
                            tone={
                              Math.abs(dp) <= 0.1
                                ? "good"
                                : Math.abs(dp) <= 0.25
                                  ? "warn"
                                  : "bad"
                            }
                          />
                        ) : (
                          "—"
                        )}
                      </Num>
                      <Num muted>{pm ? "—" : pct(ch.planMargin, 1)}</Num>
                      <Num muted>
                        {pm || !r.plannedSale ? "—" : pct(r.plannedMargin, 1)}
                      </Num>
                      <Num sx={{ fontWeight: 600 }}>
                        {pm || !r.sale ? "—" : pct(r.margin, 1)}
                      </Num>
                      <Num>
                        {pm || !r.sale || !Number.isFinite(ch.planMargin) ? (
                          "—"
                        ) : (
                          <Signal
                            text={pp(r.margin - ch.planMargin)}
                            tone={toneOfMarginDiff(r.margin - ch.planMargin)}
                          />
                        )}
                      </Num>
                      <Num>
                        {pm || !r.sale || !r.plannedSale ? (
                          "—"
                        ) : (
                          <Signal
                            text={pp(r.margin - r.plannedMargin)}
                            tone={toneOfMarginDiff(r.margin - r.plannedMargin)}
                          />
                        )}
                      </Num>
                    </TableRow>
                    {open.has(key) &&
                      subs.map(({ s, t }) => (
                        <TableRow
                          key={`${c}-${s}`}
                          sx={{ backgroundColor: theme.palette.grey[50] }}
                        >
                          <TableCell sx={{ pl: 7 }}>
                            {subformatLabel(s)}{" "}
                            <Typography
                              component="span"
                              variant="caption"
                              color="text.secondary"
                            >
                              {pct(r.sale ? t.sale / r.sale : NaN, 0)} del canal
                            </Typography>
                          </TableCell>
                          <Num muted>—</Num>
                          <Num>{eur(t.sale)}</Num>
                          <Num muted>—</Num>
                          <Num muted>—</Num>
                          <Num muted>—</Num>
                          <Num muted>
                            {pm || !t.plannedSale
                              ? "—"
                              : pct(t.plannedMargin, 1)}
                          </Num>
                          <Num>{pm || !t.sale ? "—" : pct(t.margin, 1)}</Num>
                          <Num muted>—</Num>
                          <Num>
                            {pm || !t.sale || !t.plannedSale ? (
                              "—"
                            ) : (
                              <Signal
                                text={pp(t.margin - t.plannedMargin)}
                                tone={toneOfMarginDiff(
                                  t.margin - t.plannedMargin
                                )}
                              />
                            )}
                          </Num>
                        </TableRow>
                      ))}
                  </React.Fragment>
                );
              })}
              <TableRow sx={{ "& td": { fontWeight: 700 } }}>
                <TableCell>Total</TableCell>
                <Num>{eur(b.planInvestment)}</Num>
                <Num>{eur(b.realInvestment)}</Num>
                <Num>{eurSigned(b.realInvestment - b.planInvestment)}</Num>
                <Num>{b.planInvestment ? pctSigned(compliance - 1) : "—"}</Num>
                <Num>
                  {pct(
                    b.planInvestment ? b.planProfit / b.planInvestment : NaN,
                    1
                  )}
                </Num>
                <Num>{pct(totals.plannedMargin, 1)}</Num>
                <Num>{pct(totals.margin, 1)}</Num>
                <Num />
                <Num />
              </TableRow>
            </TableBody>
          </Table>
        </Box>
      </Section>

      <Section
        title="Account: Business Plan frente a lo ejecutado"
        subtitle={`${accounts} decide cómo se reparte la inversión del cliente. Se compara el reparto del Business Plan con el que se está ejecutando ${period}.`}
      >
        <Box
          sx={{
            display: "grid",
            gap: 3,
            gridTemplateColumns: { xs: "1fr", md: "1.2fr 1fr" },
            alignItems: "center",
          }}
        >
          <Stack
            direction="row"
            gap={4}
            justifyContent="space-around"
            flexWrap="wrap"
          >
            {[
              { label: "Business Plan", share: b.planShareCM },
              { label: "Real", share: b.realShareCM },
            ].map((s) => (
              <Stack key={s.label} alignItems="center" gap={0.5}>
                <ShareRing share={s.share} size={150} />
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  {s.label}
                </Typography>
              </Stack>
            ))}
            <Box
              sx={{
                flexBasis: "100%",
                display: "flex",
                justifyContent: "center",
              }}
            >
              <TypeLegend />
            </Box>
          </Stack>
          <Box
            sx={{
              backgroundColor: theme.palette.grey[50],
              borderRadius: 3,
              p: 2.5,
            }}
          >
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontWeight: 600 }}
            >
              Compra de medios frente a Paid Media
            </Typography>
            {!Number.isFinite(shift) ? (
              <Typography variant="body2" color="text.secondary">
                Sin Business Plan para comparar.
              </Typography>
            ) : Math.abs(shift) < 0.005 ? (
              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                Igual que el plan
              </Typography>
            ) : (
              <>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 700,
                    color: toneColor(b.typeEffect >= 0 ? "good" : "bad"),
                  }}
                >
                  {pp(shift, 1)}
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  {shift > 0 ? "Más" : "Menos"} peso en compra de medios que en
                  el plan, a favor de{" "}
                  {shift > 0 ? "compra de medios" : "Paid Media"}.
                </Typography>
                <Box mt={1.5}>
                  <Signal
                    text={`${eurSigned(b.typeEffect)} de beneficio bruto`}
                    tone={b.typeEffect >= 0 ? "good" : "bad"}
                  />
                </Box>
              </>
            )}
          </Box>
        </Box>

        <Typography variant="subtitle2" sx={{ fontWeight: 600, mt: 3 }}>
          Por formato
        </Typography>
        <Note>
          Peso de cada formato en la inversión del plan y en la real. El importe
          es lo que suma o resta al beneficio bruto ese cambio de peso, con el
          margen del plan de cada formato.
        </Note>
        <Box
          sx={{
            display: "grid",
            gap: 1.5,
            mt: 1.5,
            gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
          }}
        >
          {shown
            .map((c) => b.channels[c])
            .sort((x, y) => y.realShare - x.realShare)
            .map((ch) => {
              const tone =
                Math.abs(ch.mix) < 1 ? "none" : ch.mix > 0 ? "good" : "bad";
              const label = !ch.planInvestment
                ? "Fuera del plan"
                : !ch.real.sale
                  ? "No activado"
                  : null;
              return (
                <Box
                  key={ch.channel}
                  sx={{
                    border: `1px solid ${theme.palette.grey[200]}`,
                    borderRadius: 2,
                    p: 1.5,
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    <ChannelSwatch channel={ch.channel} />
                    {ch.channel}
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 600, lineHeight: 1.2 }}
                  >
                    {pct(ch.realShare, 0)}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                  >
                    plan {pct(ch.planShare, 0)} ·{" "}
                    {pp(ch.realShare - ch.planShare, 1)}
                    {label ? ` · ${label}` : ""}
                  </Typography>
                  <Box mt={0.5}>
                    <Signal text={eurSigned(ch.mix)} tone={tone} />
                  </Box>
                </Box>
              );
            })}
        </Box>
      </Section>

      <ClientDetailTables
        data={data}
        rows={rows}
        allRows={allRows}
        month={month}
      />
    </Stack>
  );
};

const Ranking: React.FC<{
  data: TabProps["data"];
  month: number;
  sort: "real" | "compliance" | "shift";
  onSort: (s: "real" | "compliance" | "shift") => void;
  onOpenClient: (id: string) => void;
  period: string;
}> = ({ data, month, sort, onSort, onOpenClient, period }) => {
  const theme = useTheme();
  const toneColor = useToneColor();
  const list = useMemo(() => {
    const rows = inPeriod(data.rows, month, data.lastMonth);
    const plan = inPeriod(data.plan, month, data.lastMonth);
    const rowsBy = groupBy(rows, (r) => r.clientId);
    const planBy = groupBy(plan, (l) =>
      data.clientOf(l.companyId, l.companyName || l.clientName)
    );
    const ids = new Set([...rowsBy.keys(), ...planBy.keys()]);
    return [...ids].map((id) => {
      const b = bridge(rowsBy.get(id) ?? [], planBy.get(id) ?? []);
      const accounts = [
        ...new Set((rowsBy.get(id) ?? []).flatMap((r) => r.accounts)),
      ];
      return {
        id,
        name: data.clientName(id),
        accounts: peopleLabel(accounts),
        b,
        compliance: b.planInvestment
          ? b.realInvestment / b.planInvestment
          : NaN,
        shift:
          (Number.isFinite(b.realShareCM) ? b.realShareCM : 0) -
          (Number.isFinite(b.planShareCM) ? b.planShareCM : 0),
        hasRows: rowsBy.has(id),
      };
    });
  }, [data, month]);

  const sorted = [...list].sort((a, b) =>
    sort === "compliance"
      ? (b.compliance || 0) - (a.compliance || 0)
      : sort === "shift"
        ? b.shift - a.shift
        : b.b.realInvestment - a.b.realInvestment
  );
  const totalPlan = sum(list, (o) => o.b.planInvestment);
  const totalReal = sum(list, (o) => o.b.realInvestment);
  const reached = list.filter((o) => o.compliance >= 0.98).length;
  const maxCompliance = Math.max(
    1.2,
    ...list.map((o) => (Number.isFinite(o.compliance) ? o.compliance : 0))
  );

  return (
    <Section
      title="Resumen de clientes"
      subtitle={`${list.length} clientes ${period}: ${keur(totalReal)} facturados frente a ${keur(totalPlan)} del Business Plan. ${reached} ${reached === 1 ? "llega" : "llegan"} al plan. Pulsa un cliente para ver su detalle.`}
      action={
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel id="cb-rank-sort">Ordenar por</InputLabel>
          <Select
            labelId="cb-rank-sort"
            label="Ordenar por"
            value={sort}
            onChange={(e) => onSort(e.target.value as typeof sort)}
          >
            <MenuItem value="real">Facturación real</MenuItem>
            <MenuItem value="compliance">Cumplimiento del BP</MenuItem>
            <MenuItem value="shift">Movimiento a compra de medios</MenuItem>
          </Select>
        </FormControl>
      }
    >
      <TypeLegend />
      <Box sx={{ overflowX: "auto", mt: 1 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>#</TableCell>
              <TableCell>Cliente</TableCell>
              <TableCell>Account</TableCell>
              <Num>Business Plan</Num>
              <Num>Real</Num>
              <TableCell sx={{ minWidth: 180 }}>Cumplimiento</TableCell>
              <Num>Compra de medios en BP</Num>
              <Num>Compra de medios real</Num>
              <Num>Diferencia</Num>
            </TableRow>
          </TableHead>
          <TableBody>
            {sorted.map((o, i) => {
              const tone = toneOfCompliance(o.compliance);
              return (
                <TableRow
                  key={o.id}
                  hover
                  onClick={() => o.hasRows && onOpenClient(o.id)}
                  sx={{ cursor: o.hasRows ? "pointer" : "default" }}
                >
                  <TableCell sx={{ color: "text.secondary" }}>
                    {i + 1}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{o.name}</TableCell>
                  <TableCell sx={{ color: "text.secondary" }}>
                    {o.accounts}
                  </TableCell>
                  <Num muted>{eur(o.b.planInvestment)}</Num>
                  <Num sx={{ fontWeight: 600 }}>{eur(o.b.realInvestment)}</Num>
                  <TableCell>
                    <Stack direction="row" alignItems="center" gap={1}>
                      <ComplianceBar
                        value={o.compliance}
                        max={maxCompliance}
                        color={toneColor(tone) ?? theme.palette.grey[400]}
                      />
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 600, color: toneColor(tone) }}
                      >
                        {o.b.planInvestment
                          ? pct(o.compliance, 0)
                          : "fuera del BP"}
                      </Typography>
                    </Stack>
                  </TableCell>
                  <Num>
                    <MixBar shareCM={o.b.planShareCM} />
                  </Num>
                  <Num>
                    <MixBar shareCM={o.b.realShareCM} />
                  </Num>
                  <Num>
                    {Number.isFinite(o.b.planShareCM) &&
                    Number.isFinite(o.b.realShareCM) ? (
                      <Signal
                        text={pp(o.shift, 1)}
                        tone={toneOfMarginDiff(o.shift)}
                      />
                    ) : (
                      "—"
                    )}
                  </Num>
                </TableRow>
              );
            })}
            <TableRow sx={{ "& td": { fontWeight: 700 } }}>
              <TableCell />
              <TableCell>Total</TableCell>
              <TableCell />
              <Num>{eur(totalPlan)}</Num>
              <Num>{eur(totalReal)}</Num>
              <TableCell>
                {pct(totalPlan ? totalReal / totalPlan : NaN, 0)}
              </TableCell>
              <Num />
              <Num />
              <Num />
            </TableRow>
          </TableBody>
        </Table>
      </Box>
      {data.unmappedPlan.length > 0 && (
        <Note>
          {
            new Set(data.unmappedPlan.map((l) => `${l.clientName}|${l.brand}`))
              .size
          }{" "}
          parejas cliente + marca del Business Plan no están emparejadas con
          ninguna empresa y no entran en la comparación. Se emparejan en la
          página del Business Plan.
        </Note>
      )}
      {!data.hasPlan && (
        <Note>
          No hay Business Plan subido para {data.year}: solo se ve lo real.
        </Note>
      )}
      <Note>
        Compra de medios = {CM_CHANNELS.join(", ")}. Paid Media = Google Ads y
        Social Ads.
      </Note>
    </Section>
  );
};

export default ClientTab;
